<template>
  <div
    :class="[
      'date-picker',
      'align-center',
      { 'is-footer': showFooter },
      { 'listing-filter-container ms-2 me-3': isListingsFilters },
    ]"
  >
    <date-range-picker
      ref="picker"
      class="flex w-full ltr"
      auto-apply
      :style="{ '--actions-color': actionsColor }"
      :single-date-picker="
        isHotelSite || isMobileIFrameView ? 'range' : !isRange
      "
      :class="{
        'iframe-date-picker': isMobileIFrameView,
        'hotel-date-picker': isHotelSite,
      }"
      :ranges="false"
      :date-range="value"
      :min-date="minCalendarDay"
      :max-date="max"
      :opens="opens"
      @select="save"
      @update="keepPickerOpen"
      @toggle="keepPickerOpen"
    >
      <template v-if="isMobile" #header>
        <div class="ma-2 text-start">
          <v-btn
            class="grey lighten-4"
            icon
            elevation="0"
            small
            @click="$refs.picker?.togglePicker(false)"
          >
            <v-icon color="black" small>$chevron_left</v-icon>
          </v-btn>
        </div>
      </template>
      <template #input>
        <div class="d-flex justify-space-between">
          <v-icon
            v-if="isListingsFilters && !isMobile"
            size="18"
            class="mr-2 grey--text text--darken-2"
            >mdi-calendar-blank</v-icon
          >
          <div v-if="isCustom" class="flex">
            <slot></slot>
          </div>
          <div v-else>
            <div v-if="value.startDate">
              <span v-if="isRange">
                {{ parseDate(value.startDate, "D MMMM") }} -
                {{ parseDate(value.endDate, "D MMMM") }}
              </span>
              <span v-else>
                {{ parseDate(value.startDate) }}
              </span>
            </div>
            <div v-else>{{ $t("select_dates") }}</div>
          </div>
        </div>
      </template>
      <template #date="data">
        <div
          @click="blockSelect($event, data.date)"
          :class="[
            'data-cell',
            { rounded: rounded },
            { 'lighten-4 success-text': daysRates },
            dataStatus(data),
          ]"
        >
          <div>{{ data.date.getDate() }}</div>
          <!-- lock icons for restricted dates -->
          <div v-if="showNoCheckIcon(data.date)" class="data-cell-icon mt-1">
            {{ noCheckIcon(data.date) }}
          </div>
        </div>
      </template>
      <template #footer v-if="isMobile && showFooter">
        <footer class="date-picker-footer">
          {{ $t("prices_and_calculations") }}
        </footer>
      </template>
    </date-range-picker>
    <v-btn
      v-if="value.startDate && clearable"
      class="ml-2"
      icon
      small
      @click="clear"
    >
      <v-icon small> fa fa-times </v-icon>
    </v-btn>
  </div>
</template>

<script>
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";
import DeviceMixin from "@/mixins/device-mixin";
import FormattersMixin from "@/mixins/formatters-mixin";
import StylesMixin from "@/mixins/styles-mixin";
import { mapState } from "vuex";
import Vue from "vue";

// Create custom date range picker component
const CustomDateRangePicker = Vue.extend({
  extends: DateRangePicker,
  methods: {
    selectMonthDate() {
      const dt = this.end || new Date();
      this.changeLeftMonth({
        year: dt.getFullYear(),
        month: dt.getMonth() + 1,
      });
    },
    hoverDate(value) {
      if (this.readonly) return;

      if (this.in_selection) {
        const pickA = this.in_selection;
        const pickB = value;

        this.start = this.normalizeDatetime(
          Math.min(pickA.valueOf(), pickB.valueOf()),
          this.start
        );
        this.end = this.normalizeDatetime(
          Math.max(pickA.valueOf(), pickB.valueOf()),
          this.end
        );
      }

      this.$emit("hover-date", value);
    },
  },
});

export default {
  name: "site-date-picker",
  components: {
    DateRangePicker: CustomDateRangePicker,
  },
  mixins: [DeviceMixin, FormattersMixin, StylesMixin],
  props: {
    opens: {
      type: String,
      default: "center",
    },
    isRange: {
      type: Boolean,
      default: false,
    },
    max: {
      default: null,
    },
    value: [Object, String],
    rounded: {
      type: Boolean,
      default: false,
    },
    showFooter: {
      type: Boolean,
      default: false,
    },
    isCustom: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    daysRates: {
      type: [],
      default: null,
    },
    isMultiUnit: {
      type: Boolean,
      default: false,
    },
    isListingsFilters: {
      type: Boolean,
      default: false,
    },
    isHotelSite: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    const { check_in, check_out } = this.$route.query;
    if (this.daysRates && check_in && check_out)
      this.save({
        startDate: check_in,
        endDate: check_out,
      });
    this.keepPickerOpen();
  },
  computed: {
    ...mapState({
      settings: (state) => state.settingsModule.settings,
    }),
    minCalendarDay() {
      return this.$moment()
        .add(this.settings.min_ahead || 0, "days")
        .format("YYYY-MM-DD");
    },
    isMobileIFrameView() {
      return this.$route.name === "search-widget" && this.isMobile;
    },
  },
  methods: {
    keepPickerOpen() {
      if (!this.isHotelSite) return;
      this.$refs.picker?.togglePicker(true); // Method to keep the picker open
    },
    save(data = { startDate: null, endDate: null }) {
      const { daysRates } = this;

      // Ignore same-day clicks
      if (this.$moment(data.startDate).isSame(data.endDate, "day")) return;

      /* -------- new START/END validation for check_in / check_out ------- */
      if (daysRates) {
        const startYMD = this.parseDate(data.startDate, "YYYY-MM-DD");
        const endYMD = this.parseDate(data.endDate, "YYYY-MM-DD");
        const startRate = daysRates[startYMD] || {};
        const endRate = daysRates[endYMD] || {};

        if (startRate?.check_in === false) {
          return this.$emit(
            "update:error-text",
            this.$t("check_in_date_unavailable")
          );
        }
        if (
          endRate?.check_out === false &&
          !this.isPreviousDayAvailable(data.endDate)
        ) {
          return this.$emit(
            "update:error-text",
            this.$t("check_in_date_unavailable")
          );
        }
      }
      /* ------------------------------------------------------------------ */

      if (!daysRates) {
        this.$emit("update:value", data);
        this.$emit("change", data);
        return;
      }

      const dates = this.getDatesList(
        this.$moment(data.startDate),
        this.$moment(data.endDate)
      );

      /* existing blocked-date check remains */
      if (this.isBlockedDate(data.startDate)) {
        return this.$emit(
          "update:error-text",
          this.$t("check_in_date_unavailable")
        );
      }

      for (const d of dates) {
        const parsedDate = this.parseDate(d, "YYYY-MM-DD");
        const unavailableUnits =
          this.isMultiUnit && !daysRates[parsedDate].available_units;
        const unavailableStatus = daysRates[parsedDate].status !== "available";

        if (unavailableUnits || unavailableStatus) {
          return this.$emit(
            "update:error-text",
            this.$t("some_dates_not_available")
          );
        }
      }

      const nights = dates.length;
      if (nights === 0) {
        return this.$emit(
          "update:error-text",
          this.$t("please_select_date_range")
        );
      }

      // Minimum stay is determined by check-in date only, not subsequent dates
      const checkInDate = this.parseDate(data.startDate, "YYYY-MM-DD");
      const checkInMinNights = daysRates[checkInDate]?.minNights || 1;
      if (checkInMinNights > nights) {
        this.$emit(
          "update:error-text",
          this.$t("minimum_stay_nights", { nights: checkInMinNights })
        );
        this.$emit("update:value", data);
        this.$emit("change", data);
        return;
      }

      this.$emit("update:error-text", null);
      this.$emit("update:value", data);
      this.$emit("change", data);
    },
    clear() {
      this.save();
    },
    getDatesList(startDate, endDate) {
      const dates = [];
      while (startDate < endDate) {
        dates.push(startDate.toDate());
        startDate = startDate.clone().add(1, "days");
      }
      return dates;
    },
    blockSelect(e, date) {
      const picker = this.$refs.picker;
      const isInSelection = picker?.in_selection;

      if (this.isBlockedDate(date) && !this.isPreviousDayAvailable(date)) {
        e.stopPropagation();
      }

      if (isInSelection === false && this.isCheckoutOnly(date)) {
        e.stopPropagation();
      }

      if (typeof isInSelection === "object" && this.isCheckinOnly(date)) {
        e.stopPropagation();
      }
    },
    isBlockedDate(date) {
      const { daysRates } = this;
      const day = this.parseDate(date, "YYYY-MM-DD");
      if (!daysRates) return false;

      const rate = daysRates[day];
      const noUnits = rate && this.isMultiUnit && rate.available_units === 0;

      /* ↓   BOTH flags are explicitly false  */
      const bothClosed = rate?.check_in === false && rate?.check_out === false;

      return (
        !rate ||
        noUnits ||
        bothClosed ||
        (!this.isMultiUnit && rate.status !== "available")
      );
    },
    isPreviousDayAvailable(date) {
      const { daysRates } = this;
      const prev = this.$moment(date).subtract(1, "days").format("YYYY-MM-DD");
      const prevAvailable =
        daysRates?.[prev] && daysRates[prev].status === "available";

      return (
        prevAvailable &&
        this.isBlockedDate(date) &&
        !this.isCheckoutOnly(date) &&
        !this.isCheckinOnly(date)
      );
    },
    isCheckoutOnly(date) {
      const rate = this.daysRates?.[this.parseDate(date, "YYYY-MM-DD")];
      return rate?.check_in === false;
    },
    isCheckinOnly(date) {
      const rate = this.daysRates?.[this.parseDate(date, "YYYY-MM-DD")];
      return rate?.check_out === false && rate?.check_in !== false;
    },
    dataStatus({ date }) {
      if (this.isPreviousDayAvailable(date)) return "checkoutOnly";
      if (this.isBlockedDate(date)) return "blocked";
      if (this.isCheckoutOnly(date)) return "checkoutOnly";
      if (this.isCheckinOnly(date)) return "checkinOnly";
      return "available";
    },
    noCheckIcon(date) {
      if (this.isPreviousDayAvailable(date)) {
        return "";
      }
      if (this.isCheckoutOnly(date)) {
        return "🚫↑";
      }
      if (this.isCheckinOnly(date)) {
        return "🚫↓";
      }
      return "";
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    showNoCheckIcon(_date) {
      return false;
    },
  },
};
</script>

<style lang="scss">
.hotel-date-picker {
  .daterangepicker {
    box-shadow: none !important;
  }
  .calendar-table {
    color: black;
    .data-cell {
      width: 50px !important;
      height: 50px !important;
    }
  }
}
.listing-filter-container {
  border: 1px solid rgba(0, 0, 0, 0.38);
  border-radius: 4px;
  padding: 9px 0;
  background-color: white;
  height: 40px;
}
.vue-daterange-picker {
  .off {
    visibility: hidden;
  }
  .blocked {
    text-decoration: line-through;
    font-weight: 400;
    color: map-get($grey, "base");
  }
  .available {
    color: black;
    font-weight: 600;
  }
  .checkoutOnly {
    font-weight: 600;
    color: map-get($grey, "darken-1");
  }
  .checkinOnly {
    font-weight: 600;
    color: map-get($grey, "darken-1");
  }
  .daterangepicker {
    margin-top: 16px;
    border: 0;
    box-shadow: 0 10px 20px #00000019;
    &::before,
    &::after {
      display: none;
    }
    .calendars {
      background-color: #fff;
      height: auto;
    }
    .calendar-table {
      th,
      td {
        width: auto;
        height: auto;
        font-size: 13px;
        &.in-range {
          background-color: color-mix(in srgb, var(--actions-color) 10%, white);
        }
        &.active {
          .data-cell {
            background-color: var(--actions-color);
            border-radius: 40px;
            &.rounded {
              border-radius: 4px;
            }
          }
          .available,
          .checkoutOnly,
          .checkinOnly {
            color: white;
          }
        }
        &.disabled {
          background-color: #fff;
        }
        &.start-date {
          border-top-left-radius: 40px;
          border-bottom-left-radius: 40px;
        }
        &.end-date {
          border-top-right-radius: 40px;
          border-bottom-right-radius: 40px;
        }
      }
      th {
        height: 40px;
      }
      .data-cell {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        width: 40px;
        height: 40px;
        font-family: $body-font-family;
        line-height: 1.2;
        &-price {
          font-size: 11px;
        }
      }
    }
    .drp {
      &-calendar {
        width: auto;
        max-width: none;
      }
    }
  }
  .reportrange-text {
    border: 0;
    padding: 0;
  }
}
@media #{map-get($display-breakpoints, 'sm-only')} {
  .show-calendar {
    position: fixed;
  }
}

@media #{map-get($display-breakpoints, 'xs-only')} {
  .date-picker {
    &.is-footer {
      .calendars {
        margin-bottom: 72px;
      }
    }
  }
  .vue-daterange-picker {
    .daterangepicker {
      position: fixed;
      margin-top: 0;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      transform: translate(0) !important;
      width: 100%;
      height: 100vh;
      overflow-y: auto;
      &.show-calendar {
        display: flex;
      }
      .calendars {
        height: 100vh;
      }
      .calendar-table {
        th,
        td {
          width: 40px;
          height: 40px;
          padding: 0;
        }
        .data-cell {
          width: 100%;
        }
      }
    }
    .date-picker-footer {
      position: fixed;
      display: flex;
      align-items: center;
      background-color: #fff;
      padding: 16px 10px;
      height: 72px;
      bottom: 0;
      left: 0;
      right: 0;
      box-shadow: 0 -5px 10px #00000019;
    }
  }
}

.iframe-date-picker {
  .calendars-container {
    width: 100vw;
  }
  .daterangepicker {
    height: 387px;
  }
}

.data-cell-icon {
  font-size: 11px;
  line-height: 1;
}
</style>
